import Tooltip from '../Tooltip/Tooltip';

type RankStats = {
  certificateScore: number;
  hoursScore: number;
  locationScore: number;
  quoteScore: number;
  ratingsScore: number;
  reviewsScore: number;
  totalPilotsConsidered: number;
};

type AwardProps = {
  value: number; // overall rating in [0, 1], displayed as 1–10
  stats?: RankStats;
  openPilot: boolean;
};

function Award({ value, stats, openPilot }: AwardProps) {
  const squares = [
    {
      rank: 0.7,
      background: `
        radial-gradient(circle at top left, rgba(255, 255, 255, 0.8), transparent 40%),
        linear-gradient(135deg, #E9CF84, #F0E69D, #DEB16A)
      `,
    },
    {
      rank: 0.4,
      background: `
        radial-gradient(circle at top left, rgba(255, 255, 255, 0.8), transparent 40%),
        linear-gradient(135deg, #B5B5B5, #EAEAEA, #DADADA)
      `,
    },
    {
      rank: 0,
      background: `
        radial-gradient(circle at top left, rgba(255, 255, 255, 0.8), transparent 40%),
        linear-gradient(135deg, #DA9565, #FAC993, #DA9867)
      `,
    },
  ];
  // Safely extract the relevant scores or default to 0 if stats is missing
  const hoursScore = stats?.hoursScore ?? 0;
  const locationScore = stats?.locationScore ?? 0;
  const quoteScore = stats?.quoteScore ?? 0;

  const getSquare = (value: number) => {
    if (value >= 0.7) {
      return squares[0];
    }
    if (value < 0.7 && value >= 0.4) {
      return squares[1];
    }
    return squares[2];
  };
  // Clamp a score to [0, 1]
  const clamp = (val: number) => Math.min(Math.max(val, 0), 1);

  // Build an array of 10 squares, marking them green or black
  // based on how many squares should be "filled" (green).
  const renderSquares = (score: number) => {
    const clamped = clamp(score);
    const filledCount = Math.round(clamped * 10); // how many green squares

    return Array.from({ length: 10 }, (_, idx) => {
      const isGreen = idx < filledCount;
      return (
        <div
          key={idx}
          className={`w-2 h-2 mr-1 ${isGreen ? 'bg-green-500' : 'bg-black'}`}
        />
      );
    });
  };

  // Convert the 0–1 "value" to 1–10 and display it with one decimal place
  const ratingDisplay = (value * 10).toFixed(1);

  return (
    <div className={`absolute ${openPilot ? 'top-12' : 'top-2'} right-2`}>
      <Tooltip
        direction="left"
        trigger={
          <div className="border border-gray-300 shadow-lg p-2 bg-white rounded w-32">
            {/* Overall rating row */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-700 font-semibold">Rating</span>
              <div
                className="px-2 py-1 flex justify-center items-center text-black font-bold text-xl border border-gray-300"
                style={{
                  backgroundImage: getSquare(value).background,
                  boxShadow:
                    'inset 2px 2px 6px rgba(0,0,0,0.3), 2px 2px 8px rgba(0,0,0,0.2)',
                }}
              >
                {ratingDisplay}
              </div>
            </div>

            {/* Experience row */}
            <div className="text-xs font-semibold text-gray-700">
              Experience
            </div>
            <div className="flex mb-1">{renderSquares(hoursScore)}</div>

            {/* Location row */}
            <div className="text-xs font-semibold text-gray-700">Location</div>
            <div className="flex mb-1">{renderSquares(locationScore)}</div>

            {/* Quote row */}
            <div className="text-xs font-semibold text-gray-700">Quote</div>
            <div className="flex">{renderSquares(quoteScore)}</div>
          </div>
        }
      >
        This score (1–10) ranks pilots based on hours, certificates/ratings,
        reviews, location, and quote.
      </Tooltip>
    </div>
  );
}

export default Award;
